import { OnlineBookingState } from 'reducers/onlineBooking/OnlineBookingState';
import { Action } from 'redux';
import { AddonService } from 'types/AddonService';
import { AgreementSignature } from 'types/Agreement';
import { AvailabilityHour } from 'types/Availability';
import { Customer } from 'types/Customer';
import { OnlineBookingItem, OnlineBookingStep } from 'types/OnlineBooking';
import { Pet } from 'types/Pet';
import { Service } from 'types/Service';
import { Staff } from 'types/Staff';

export const ONLINE_BOOKING_RESET = 'online_booking/reset';
export const ONLINE_BOOKING_ERROR = 'online_booking/error';
export const ONLINE_BOOKING_SET_LOADING = 'online_booking/set_loading';

// Step
export const ONLINE_BOOKING_SET_STEP = 'online_booking/set_step';

// Customer
export const ONLINE_BOOKING_SET_CUSTOMER = 'online_booking/set_customer';

// Booking items
export const ONLINE_BOOKING_SET_BOOKING_ITEMS = 'online_booking/set_booking_items';
export const ONLINE_BOOKING_SET_BOOKING_ITEM_SERVICE = 'online_booking/set_booking_item_service';
export const ONLINE_BOOKING_TOGGLE_BOOKING_ITEM_ADDON = 'online_booking/toggle_booking_item_addon';

// Staffs
export const ONLINE_BOOKING_SET_STAFFS = 'online_booking/set_staffs';
export const ONLINE_BOOKING_SET_STAFFS_LOADING = 'online_booking/set_staffs_loading';
export const ONLINE_BOOKING_SET_SELECTED_STAFF_ID = 'online_booking/set_selected_staff_id';
export const ONLINE_BOOKING_SET_STAFFS_NEXT_AVAILABLE_DATE =
    'online_booking/set_staffs_next_available_date';
export const ONLINE_BOOKING_SET_STAFFS_FETCHED = 'online_booking/set_staffs_fetched';
export const ONLINE_BOOKING_SET_STAFFS_BUSY = 'online_booking/set_staffs_busy';

// Availability
export const ONLINE_BOOKING_SET_AVAILABILITY = 'online_booking/set_availability';

// Date & time
export const ONLINE_BOOKING_SET_DATE = 'online_booking/set_date';
export const ONLINE_BOOKING_SET_TIME = 'online_booking/set_time';

// Agreements
export const ONLINE_BOOKING_SET_AGREEMENT_SIGNATURES = 'online_booking/set_agreement_signatures';
export const ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE = 'online_booking/add_agreement_signature';

export const ONLINE_BOOKING_ADD_PET = 'online_booking/add_pet';

export const ONLINE_BOOKING_UPDATE_PET_START = 'online_booking/update_pet_start';
export const ONLINE_BOOKING_UPDATE_PET_SUCCESS = 'online_booking/update_pet_success';

interface OnlineBookingResetAction extends Action<typeof ONLINE_BOOKING_RESET> {}

interface OnlineBookingErrorAction extends Action<typeof ONLINE_BOOKING_ERROR> {}

interface OnlineBookingSetLoadingAction extends Action<typeof ONLINE_BOOKING_SET_LOADING> {
    payload: {
        loading: boolean;
    };
}

interface OnlineBookingSetStepAction extends Action<typeof ONLINE_BOOKING_SET_STEP> {
    payload: {
        step: OnlineBookingStep;
    };
}

interface OnlineBookingSetCustomerAction extends Action<typeof ONLINE_BOOKING_SET_CUSTOMER> {
    payload: {
        customer: Customer;
        isReturningClient: boolean;
    };
}

interface OnlineBookingAddPetAction extends Action<typeof ONLINE_BOOKING_ADD_PET> {
    payload: {
        pet: Pet;
    };
}

interface OnlineBookingSetBookingItemsAction
    extends Action<typeof ONLINE_BOOKING_SET_BOOKING_ITEMS> {
    payload: {
        items: Array<OnlineBookingItem>;
    };
}

interface OnlineBookingSetBookingItemServiceAction
    extends Action<typeof ONLINE_BOOKING_SET_BOOKING_ITEM_SERVICE> {
    payload: {
        petUuid: string;
        service: Service;
    };
}

interface OnlineBookingToggleBookingItemAddonAction
    extends Action<typeof ONLINE_BOOKING_TOGGLE_BOOKING_ITEM_ADDON> {
    payload: {
        petUuid: string;
        addon: AddonService;
        isSelected: boolean;
    };
}

interface OnlineBookingUpdatePetStartAction
    extends Action<typeof ONLINE_BOOKING_UPDATE_PET_START> {}

interface OnlineBookingUpdatePetSuccessAction
    extends Action<typeof ONLINE_BOOKING_UPDATE_PET_SUCCESS> {
    payload: {
        pet: Pet;
    };
}

interface OnlineBookingSetStaffsAction extends Action<typeof ONLINE_BOOKING_SET_STAFFS> {
    payload: {
        staffs: Array<Staff>;
    };
}

interface OnlineBookingSetSelectedStaffIdAction
    extends Action<typeof ONLINE_BOOKING_SET_SELECTED_STAFF_ID> {
    payload: {
        staffId: OnlineBookingState['selectedStaffId'];
    };
}

interface OnlineBookingSetStaffsLoadingAction
    extends Action<typeof ONLINE_BOOKING_SET_STAFFS_LOADING> {
    payload: {
        loadingState: Map<number, boolean> | boolean;
    };
}

interface OnlineBookingSetStaffsNextAvailableDateAction
    extends Action<typeof ONLINE_BOOKING_SET_STAFFS_NEXT_AVAILABLE_DATE> {
    payload: {
        hours: Array<AvailabilityHour>;
    };
}

interface OnlineBookingSetStaffsFetchedAction
    extends Action<typeof ONLINE_BOOKING_SET_STAFFS_FETCHED> {
    payload: {
        staffIds: Array<number>;
    };
}

interface OnlineBookingSetStaffsBusyAction extends Action<typeof ONLINE_BOOKING_SET_STAFFS_BUSY> {
    payload: {
        staffs: Array<{
            staffId: number;
            weeksCount: number;
        }>;
    };
}

interface OnlineBookingSetAvailabilityAction
    extends Action<typeof ONLINE_BOOKING_SET_AVAILABILITY> {
    payload: {
        availability: Array<AvailabilityHour>;
    };
}

interface OnlineBookingSetDateAction extends Action<typeof ONLINE_BOOKING_SET_DATE> {
    payload: {
        date: OnlineBookingState['date'];
    };
}

interface OnlineBookingSetTimeAction extends Action<typeof ONLINE_BOOKING_SET_TIME> {
    payload: {
        time: OnlineBookingState['time'];
    };
}

interface OnlineBookingSetAgreementSignaturesAction
    extends Action<typeof ONLINE_BOOKING_SET_AGREEMENT_SIGNATURES> {
    payload: {
        signatures: Array<AgreementSignature>;
    };
}

interface OnlineBookingAddAgreementSignatureAction
    extends Action<typeof ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE> {
    payload: {
        signature: AgreementSignature;
    };
}

export type OnlineBookingAction =
    | OnlineBookingResetAction
    | OnlineBookingErrorAction
    | OnlineBookingSetLoadingAction
    | OnlineBookingSetStepAction
    // Customer
    | OnlineBookingSetCustomerAction
    // Booking items
    | OnlineBookingSetBookingItemsAction
    | OnlineBookingSetBookingItemServiceAction
    | OnlineBookingToggleBookingItemAddonAction
    // Staffs
    | OnlineBookingSetStaffsAction
    | OnlineBookingSetSelectedStaffIdAction
    | OnlineBookingSetStaffsLoadingAction
    | OnlineBookingSetStaffsNextAvailableDateAction
    | OnlineBookingSetStaffsFetchedAction
    | OnlineBookingSetStaffsBusyAction
    // Availability
    | OnlineBookingSetAvailabilityAction
    // Date & time
    | OnlineBookingSetDateAction
    | OnlineBookingSetTimeAction
    // Agreements
    | OnlineBookingSetAgreementSignaturesAction
    | OnlineBookingAddAgreementSignatureAction
    // Pets
    | OnlineBookingAddPetAction
    | OnlineBookingUpdatePetStartAction
    | OnlineBookingUpdatePetSuccessAction;
